/**
 * <HealthAlertBanner /> — drop-in health alert for Sales/Expense screens.
 *
 * Reuses the SAME canonical data source as the Center Profitability Health
 * Dashboard (POST /api/health-dashboard/score) so the alert score and
 * narrative are always identical to what the owner sees there.
 *
 * Behaviour:
 *   • score >= 80  → renders a tiny green "Healthy" pill (or nothing if hideWhenHealthy)
 *   • score 60-79  → amber Warning banner
 *   • score < 60   → red Critical banner
 *   • center === "all" / missing center → renders nothing
 *
 * Two visual variants:
 *   • variant="full"     long banner with founder narrative + score tile
 *                         (Sales & Cash Dashboard, Sales Overview, Daily Report)
 *   • variant="compact"  card with score badge + key metrics + recommended action
 *                         (Expense Entry Grid, Expense Heads / Category view)
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, Brain, ShieldAlert, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

import { resolveCurrency } from "../utils/money";
const sym = (currency) => resolveCurrency(currency).symbol;
const fmt = (n, currency) =>
  `${sym(currency)}${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

function tierFromScore(score) {
  if (score == null) return null;
  if (score >= 80) return "Healthy";
  if (score >= 60) return "Warning";
  return "Critical";
}

const TIER_STYLES = {
  Healthy: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-800",
    accent: "bg-emerald-500",
    icon: CheckCircle2,
  },
  Warning: {
    bg: "bg-amber-50",
    border: "border-amber-300",
    text: "text-amber-900",
    accent: "bg-amber-500",
    icon: AlertTriangle,
  },
  Critical: {
    bg: "bg-red-50",
    border: "border-red-300",
    text: "text-red-900",
    accent: "bg-red-500",
    icon: ShieldAlert,
  },
};

function useHealth(token, center, month) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!token || !center || center === "all" || !month) {
      setData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/health-dashboard/score`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, center, month }),
        });
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, center, month]);
  return { data, loading };
}

export default function HealthAlertBanner({
  token,
  center,
  month,
  variant = "compact",
  hideWhenHealthy = false,
  className = "",
}) {
  const { data, loading } = useHealth(token, center, month);
  if (loading || !data || !data.score) return null;

  const score = Math.round(data.score.score ?? 0);
  const tier = tierFromScore(score);
  if (!tier) return null;
  if (tier === "Healthy" && hideWhenHealthy) return null;

  const styles = TIER_STYLES[tier];
  const Icon = styles.icon;
  const currency = data.currency || "INR";
  const cur = data.current || {};
  const prev = data.previous || null;
  const wc = data.wc || {};

  // Derived metrics
  const salesNow = cur?.sales?.total || 0;
  const salesPrev = prev?.sales?.total || 0;
  const salesDelta = salesPrev ? ((salesNow - salesPrev) / salesPrev) * 100 : null;
  const expenses = cur?.expenses_total || 0;
  const expensePctOfRevenue = cur?.net_revenue
    ? (expenses / cur.net_revenue) * 100
    : null;
  const wcStatus = wc.status || (wc.current_wc > 0 ? "OK" : "Depleted");

  const primaryRisk =
    salesDelta != null && salesDelta < -10
      ? `Sales drop ${pct(salesDelta)} vs ${prev?.label || "prev"}`
      : expensePctOfRevenue && expensePctOfRevenue > 80
      ? `Expenses at ${pct(expensePctOfRevenue)} of net revenue`
      : (wc.wc_pct ?? 100) < 30
      ? `Working capital at ${pct(wc.wc_pct)} of base`
      : "Margins under pressure";

  const recommendedAction =
    tier === "Critical"
      ? "Freeze discretionary spend. Audit rent, manpower, aggregator burden today."
      : "Control expenses; review rent, utilities, manpower & aggregator costs before approving new spend.";

  const generatedAt = data.generated_at
    ? new Date(data.generated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";

  // Healthy pill (only renders if hideWhenHealthy === false)
  if (tier === "Healthy") {
    return (
      <div
        className={`inline-flex items-center gap-2 rounded-full border ${styles.border} ${styles.bg} ${styles.text} px-3 py-1 text-xs font-semibold ${className}`}
        data-testid="health-alert-healthy"
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        Health Score: {score}/100 · Healthy
      </div>
    );
  }

  // ──────────────────── FULL variant ────────────────────
  if (variant === "full") {
    return (
      <div
        className={`rounded-2xl border-2 ${styles.border} ${styles.bg} p-5 mb-4 shadow-sm ${className}`}
        data-testid={`health-alert-${tier.toLowerCase()}`}
      >
        <div className="flex flex-wrap items-start gap-4">
          {/* Score tile */}
          <div className={`shrink-0 rounded-xl bg-white border ${styles.border} p-3 text-center min-w-[110px]`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Health Score</div>
            <div className={`text-4xl font-black ${styles.text} leading-none mt-1`}>{score}</div>
            <div className={`text-[10px] mt-1 ${styles.text} font-bold`}>/ 100</div>
            <div className={`inline-flex items-center gap-1 mt-2 text-[10px] font-bold uppercase ${styles.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${styles.accent} animate-pulse`} />
              {tier}
            </div>
          </div>

          {/* Narrative */}
          <div className="flex-1 min-w-[260px]">
            <div className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide ${styles.text} mb-2`}>
              <Icon className="w-4 h-4" />
              Founder&apos;s Monthly Reality Check
            </div>
            <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">
              {data.narrative?.founder_summary || "AI summary unavailable for this period."}
            </p>
            <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
              <Brain className="w-3 h-3" /> AI-assisted · Generated {generatedAt}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────── COMPACT variant ────────────────────
  return (
    <div
      className={`rounded-xl border ${styles.border} ${styles.bg} p-4 mb-4 ${className}`}
      data-testid={`health-alert-${tier.toLowerCase()}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className={`shrink-0 rounded-lg bg-white border ${styles.border} px-3 py-2 text-center min-w-[80px]`}>
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Score</div>
          <div className={`text-2xl font-black ${styles.text} leading-none`}>{score}</div>
          <div className={`text-[9px] ${styles.text} font-bold mt-0.5`}>{tier}</div>
        </div>

        <div className="flex-1 min-w-[240px]">
          <div className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide ${styles.text}`}>
            <Icon className="w-4 h-4" />
            Expense Control Alert
          </div>
          <p className="text-xs text-slate-700 mt-1">
            Sales are under pressure while expenses remain high. Review rent, utilities, manpower, food cost and
            aggregator-linked costs before approving additional spending.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-[11px]">
            <div className="bg-white/70 rounded-md px-2 py-1.5 border border-white">
              <div className="text-slate-500 uppercase tracking-wide text-[9px]">Sales Drop</div>
              <div className={`font-bold ${salesDelta != null && salesDelta < 0 ? "text-red-700" : "text-slate-700"}`}>
                {salesDelta != null ? pct(salesDelta) : "—"}
              </div>
            </div>
            <div className="bg-white/70 rounded-md px-2 py-1.5 border border-white">
              <div className="text-slate-500 uppercase tracking-wide text-[9px]">Expense % of Rev</div>
              <div className="font-bold text-slate-800">
                {expensePctOfRevenue != null ? pct(expensePctOfRevenue) : "—"}
              </div>
            </div>
            <div className="bg-white/70 rounded-md px-2 py-1.5 border border-white">
              <div className="text-slate-500 uppercase tracking-wide text-[9px]">Working Capital</div>
              <div
                className={`font-bold ${
                  wc.wc_pct >= 50 ? "text-emerald-700" : wc.wc_pct >= 0 ? "text-amber-700" : "text-red-700"
                }`}
              >
                {fmt(wc.current_wc, currency)} · {wcStatus}
              </div>
            </div>
            <div className="bg-white/70 rounded-md px-2 py-1.5 border border-white">
              <div className="text-slate-500 uppercase tracking-wide text-[9px]">Primary Risk</div>
              <div className={`font-bold ${styles.text}`}>{primaryRisk}</div>
            </div>
          </div>

          <div className={`mt-3 text-xs font-semibold ${styles.text} flex items-start gap-1`}>
            <span className="shrink-0">Action:</span>
            <span className="font-medium text-slate-700">{recommendedAction}</span>
          </div>
          <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
            <Brain className="w-3 h-3" /> AI-assisted · Generated {generatedAt}
          </div>
        </div>
      </div>
    </div>
  );
}
